/** The tasks of a qualification as the slide-over edits them, and their payload. */

import {
  cleanTaskLabel,
  normalizeTaskLabel,
} from 'components/SlideOver/dataSlideOver/data/slideOverAddMission/missionTemplate/templateFormData';

// Same limits as the api (D5): a job description refuses more than 50 tasks,
// so a qualification offering more would make its templates unsavable.
export const MAX_TASK_LABEL_LENGTH = 80;
export const MAX_ACTIVE_TASKS = 50;

let draftSeed = 0;

/**
 * The active tasks of a qualification from the api, in their stored order.
 * Archived ones are dropped: they are never shown nor sent back, the api keeps
 * them as they are.
 */
export const toFormTasks = (tasks) =>
  (Array.isArray(tasks) ? tasks : [])
    .filter((task) => task && !task.archived)
    .slice()
    .sort(
      (a, b) =>
        (a.order ?? Number.MAX_SAFE_INTEGER) -
        (b.order ?? Number.MAX_SAFE_INTEGER)
    )
    .map((task) => ({
      key: `id-${task._id}`,
      _id: String(task._id),
      label: cleanTaskLabel(task.label),
      source: task.source === 'ai' ? 'ai' : 'admin',
      usedInTemplates: Number(task.usedInTemplates) || 0,
    }));

/** A task typed by the admin, not saved yet. */
export const createFormTask = (label) => {
  draftSeed += 1;
  return {
    key: `draft-${draftSeed}`,
    label: cleanTaskLabel(label),
    source: 'admin',
    usedInTemplates: 0,
  };
};

/**
 * Why a label cannot be used, as a `qualification.tasks.*` text key, or null.
 * `ignoredKey` is the task being renamed: it may keep its own label.
 */
export const getTaskLabelError = (label, tasks, ignoredKey) => {
  const cleaned = cleanTaskLabel(label);
  if (!cleaned) return 'errorEmpty';
  if (cleaned.length > MAX_TASK_LABEL_LENGTH) return 'errorTooLong';
  const wanted = normalizeTaskLabel(cleaned);
  const isDuplicate = tasks.some(
    (task) => task.key !== ignoredKey && normalizeTaskLabel(task.label) === wanted
  );
  return isDuplicate ? 'errorDuplicate' : null;
};

/** Saved tasks used by a job description need a confirmation to change. */
export const isTaskUsed = (task) =>
  Boolean(task?._id) && Number(task.usedInTemplates) > 0;

export const renameFormTask = (task, label) => {
  const cleaned = cleanTaskLabel(label);
  if (cleaned === task.label) return task;
  // An AI suggestion the admin rewrote is the admin's own task.
  return { ...task, label: cleaned, source: 'admin' };
};

export const moveFormTask = (tasks, fromIndex, toIndex) => {
  if (
    fromIndex === toIndex ||
    fromIndex < 0 ||
    toIndex < 0 ||
    fromIndex >= tasks.length ||
    toIndex >= tasks.length
  ) {
    return tasks;
  }
  const next = tasks.slice();
  const [moved] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, moved);
  return next;
};

/**
 * What postQualification / putQualification expect: the complete list of
 * active tasks in the displayed order, the api deriving `order` from it.
 */
export const buildTasksPayload = (tasks) =>
  tasks.map((task) => ({
    ...(task._id ? { _id: task._id } : {}),
    label: cleanTaskLabel(task.label),
    source: task.source === 'ai' ? 'ai' : 'admin',
  }));
